import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import type { MarketSignal } from "@/types/marketSignal.type";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import {
  DEFAULT_WINDOW_WEEKS,
  SIGNAL_KIND_OPTIONS,
  acceptWindow,
  formatShortDate,
  isEvergreenKind,
  kindLabelSingular,
} from "./signalMeta";
import KindGlyph from "./KindGlyph";
import KindSelect from "./KindSelect";
import LifetimeFields, {
  acceptWeeksInput,
  committedWeeksInput,
  resolveLifetimeChoice,
} from "./LifetimeFields";

export interface PendingSignalEdit {
  kind: string;
  title: string;
  body: string;
  /** Present only when the curator touched the window. Sending the stored value
   *  back re-validates it, and a passed or empty suggestion is refused (400). */
  recommendedValidUntil?: string | null;
}

/**
 * Inline edit form for a pending signal. It takes the place of the card's read
 * column inside the same card frame, at the same type scale, so editing reads as
 * the card opening up rather than as a different object. onSave persists through
 * the parent's updateSignal mutation.
 *
 * The window starts from acceptWindow(), the same window Accept would send, so a
 * suggestion that has already passed opens on the kind default instead of on
 * "1 week". The suggested window is only rewritten when the curator actually
 * touches it: fixing a typo in the headline never moves the date.
 *
 * The kind picker always includes the signal's current kind, first, even when it
 * is outside the seven a person can log, so editing never silently changes it.
 */
export default function EditPendingSignal({
  signal,
  busy,
  onSave,
  onCancel,
  inDeck = false,
}: {
  signal: MarketSignal;
  busy: boolean;
  onSave: (patch: PendingSignalEdit) => void;
  onCancel: () => void;
  /** Inside the phone deck's card: the mark names the kind, the actions split the foot. */
  inDeck?: boolean;
}) {
  // Snapshot once: a background refetch must not move the fields under the cursor.
  const [initial] = useState(() => {
    const w = acceptWindow(signal);
    return {
      openEnded: w.validUntil === null,
      weeksInput: String(w.weeks ?? DEFAULT_WINDOW_WEEKS),
    };
  });
  const [kind, setKind] = useState<string>(signal.kind);
  const [title, setTitle] = useState(signal.title);
  const [body, setBody] = useState(signal.body);
  const [openEnded, setOpenEnded] = useState(initial.openEnded);
  const [weeksInput, setWeeksInput] = useState(initial.weeksInput);

  // Only the evergreen kinds may run open-ended; derived rather than reset in an effect.
  const effectiveOpenEnded = openEnded && isEvergreenKind(kind);

  const kindOptions = useMemo((): { value: string; label: string }[] => {
    const opts = SIGNAL_KIND_OPTIONS.map((o) => ({ value: o.value as string, label: o.label }));
    if (!opts.some((o) => o.value === signal.kind)) {
      opts.unshift({ value: signal.kind, label: kindLabelSingular(signal.kind) });
    }
    return opts;
  }, [signal.kind]);

  const windowTouched =
    weeksInput !== initial.weeksInput || effectiveOpenEnded !== initial.openEnded;
  const ready = title.trim().length > 0 && body.trim().length > 0;
  const dirty =
    kind !== signal.kind ||
    title.trim() !== signal.title ||
    body.trim() !== signal.body ||
    windowTouched;
  const disabled = busy || !ready || !dirty;

  const idBase = `sig-edit-${signal.id}`;

  return (
    <div className="sig-edit">
      {/* Signals / Waiting card · Editing: the accent mark, then the fields. */}
      {inDeck ? (
        <p className="sig-edit__mark">
          <KindGlyph kind={signal.kind} size={14} />
          {kindLabelSingular(signal.kind)} · Editing before you decide
        </p>
      ) : (
        <p className="sig-edit__mark">
          <Pencil aria-hidden="true" />
          Editing · Proposed {formatShortDate(signal.createdAt)}
          {signal.confidence != null ? ` · ${Math.round(signal.confidence * 100)}% confidence` : null}
        </p>
      )}
      <KindSelect
        value={kind}
        onChange={setKind}
        options={kindOptions}
        id={`${idBase}-kind`}
        label="Kind"
      />

      <label className="sig-field">
        <span className="sig-field__label">Headline</span>
        <input
          className="sig-input sig-input--title"
          type="text"
          dir="auto"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="One line: what is changing?"
        />
      </label>

      <label className="sig-field">
        <span className="sig-field__label">What you're seeing</span>
        <textarea
          className="sig-input sig-textarea"
          dir="auto"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="The read, and why it matters for the work."
        />
      </label>

      <LifetimeFields
        kind={kind}
        weeksInput={weeksInput}
        openEnded={effectiveOpenEnded}
        onWeeksChange={(raw) => acceptWeeksInput(raw) && setWeeksInput(raw)}
        onWeeksBlur={() => setWeeksInput((w) => committedWeeksInput(w, initial.weeksInput))}
        onOpenEndedChange={setOpenEnded}
        label="Suggested window"
        hint={(() => {
          if (effectiveOpenEnded) return "Until a newer signal replaces it";
          const until = weeksInput === "" ? null : resolveLifetimeChoice(weeksInput, false);
          return until ? `Until ${formatShortDate(until)}` : undefined;
        })()}
      />

      <div className="sig-edit__acts">
        <KitButton variant="ghost" onClick={onCancel}>
          Cancel
        </KitButton>
        <KitButton
          variant="primary"
          isDisabled={disabled}
          onClick={() =>
            onSave({
              kind,
              title: title.trim(),
              body: body.trim(),
              ...(windowTouched
                ? { recommendedValidUntil: resolveLifetimeChoice(weeksInput, effectiveOpenEnded) }
                : {}),
            })
          }
        >
          Save
        </KitButton>
      </div>
    </div>
  );
}
